import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
// Type-only imports are erased at compile time, so they don't pull
// aws-amplify into the eager bundle. Runtime calls use `await import()`
// inside the async helpers below.
import type { AuthUser } from "aws-amplify/auth";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UserAttrs {
  email?: string;
  given_name?: string;
  family_name?: string;
}

export interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** True once Amplify has been configured against the deployed user pool. */
  isConfigured: boolean;
  userAttributes: UserAttrs | null;
  /** Refresh user state — used after sign-in / sign-out / passkey actions. */
  refresh: () => Promise<void>;
  /** Sign out of Cognito and clear local state. */
  logout: () => Promise<void>;
  /** The Cognito User Pool ID (needed for some passkey APIs). */
  userPoolId: string | null;
  /** The Cognito User Pool client ID. */
  userPoolClientId: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ---------------------------------------------------------------------------
// Email-persistence helpers — used by the login page to skip the email
// field on subsequent visits when the user has previously signed in
// successfully (with password or passkey).
// ---------------------------------------------------------------------------

const LS_REMEMBERED_EMAIL = "auth_last_email";

export const rememberEmail = (email: string): void => {
  try {
    if (email && email.trim()) {
      localStorage.setItem(LS_REMEMBERED_EMAIL, email.trim().toLowerCase());
    }
  } catch {
    /* ignore quota errors */
  }
};

export const getRememberedEmail = (): string => {
  try {
    return localStorage.getItem(LS_REMEMBERED_EMAIL) || "";
  } catch {
    return "";
  }
};

export const clearRememberedEmail = (): void => {
  try {
    localStorage.removeItem(LS_REMEMBERED_EMAIL);
  } catch {
    /* ignore */
  }
};

// ---------------------------------------------------------------------------
// Amplify config bootstrap — fetched from the build-time injected config
// ---------------------------------------------------------------------------

interface CognitoConfig {
  userPoolId: string;
  userPoolClientId: string;
}

let configurePromise: Promise<CognitoConfig | null> | null = null;

const fetchCognitoConfig = async (): Promise<CognitoConfig | null> => {
  // 1. window-injected config wins (set by inject-cognito-config.js)
  const injected = (window as unknown as { COGNITO_CONFIG?: CognitoConfig })
    .COGNITO_CONFIG;
  if (injected && injected.userPoolId && injected.userPoolClientId) {
    return {
      userPoolId: injected.userPoolId,
      userPoolClientId: injected.userPoolClientId,
    };
  }
  // 2. fall back to fetching the public JSON file
  try {
    const res = await fetch("/cognito-config.json");
    if (res.ok) {
      const data = await res.json();
      if (data?.userPoolId && data?.userPoolClientId) {
        return {
          userPoolId: data.userPoolId,
          userPoolClientId: data.userPoolClientId,
        };
      }
    }
  } catch {
    /* fall through */
  }
  return null;
};

const ensureAmplifyConfigured = (): Promise<CognitoConfig | null> => {
  if (!configurePromise) {
    configurePromise = (async () => {
      const cfg = await fetchCognitoConfig();
      if (cfg) {
        const { Amplify } = await import("aws-amplify");
        Amplify.configure({
          Auth: {
            Cognito: {
              userPoolId: cfg.userPoolId,
              userPoolClientId: cfg.userPoolClientId,
              // No `loginWith.oauth` — we don't use the Cognito Hosted UI.
            },
          },
        });
      }
      return cfg;
    })();
  }
  return configurePromise;
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [userAttributes, setUserAttributes] = useState<UserAttrs | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [userPoolId, setUserPoolId] = useState<string | null>(null);
  const [userPoolClientId, setUserPoolClientId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      // Make sure Amplify is configured before any auth call
      const cfg = await ensureAmplifyConfigured();
      if (cfg) {
        setUserPoolId(cfg.userPoolId);
        setUserPoolClientId(cfg.userPoolClientId);
        setIsConfigured(true);
      } else {
        setIsConfigured(false);
      }

      const { fetchAuthSession, fetchUserAttributes, getCurrentUser } =
        await import("aws-amplify/auth");
      // Force a fresh session to detect signed-out state quickly
      await fetchAuthSession({ forceRefresh: false });
      const current = await getCurrentUser();
      setUser(current);
      setIsAuthenticated(true);
      try {
        const attrs = await fetchUserAttributes();
        setUserAttributes({
          email: attrs.email,
          given_name: attrs.given_name,
          family_name: attrs.family_name,
        });
      } catch {
        setUserAttributes({
          email: current.signInDetails?.loginId || undefined,
        });
      }
    } catch {
      setUser(null);
      setIsAuthenticated(false);
      setUserAttributes(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      const { signOut } = await import("aws-amplify/auth");
      await signOut();
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setUserAttributes(null);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Also re-check on focus (user may have signed in/out in another tab)
    const onFocus = () => {
      refresh();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        isConfigured,
        userAttributes,
        refresh,
        logout,
        userPoolId,
        userPoolClientId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};

// Helper for pages that can't render until Amplify is wired up
export const ensureAmplifyReady = (): Promise<CognitoConfig | null> =>
  ensureAmplifyConfigured();
